import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChat } from '../context/ChatContext';
import { useAuth } from '../context/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { Check, CheckCheck, Download, ArrowLeft, Image, FileText, Video, X, Edit2, Printer } from 'lucide-react';
import ProductionPanel from './ProductionPanel';
import api from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { printOrderForm } from '../utils/orderFormPdf';
import { getDeadlineFlag, DEADLINE_STYLES } from '../utils/deadlineFlag';
import RepairEstimateDialog from './RepairEstimateDialog';
import { printRepairEstimate } from '../utils/repairSlipPrint';

// Helper to get file type from URL
const getFileType = (url) => {
  if (!url) return 'unknown';
  const lower = url.toLowerCase();
  if (lower.match(/\.(jpg|jpeg|png|gif|webp)$/)) return 'image';
  if (lower.match(/\.(mp4|mov|avi|webm)$/)) return 'video';
  if (lower.match(/\.(pdf)$/)) return 'pdf';
  return 'file';
};

// Media Gallery Component
const MediaGallery = ({ isOpen, onClose, files, orderNumber }) => {
  const images = files?.filter(f => getFileType(f) === 'image') || [];
  const videos = files?.filter(f => getFileType(f) === 'video') || [];
  const pdfs = files?.filter(f => getFileType(f) === 'pdf') || [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <span>📎 Media - {orderNumber}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Images Section */}
          {images.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                <Image className="w-4 h-4 mr-2" /> Images ({images.length})
              </h3>
              <div className="grid grid-cols-3 gap-2">
                {images.map((url, idx) => (
                  <a
                    key={idx}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="aspect-square rounded-lg overflow-hidden bg-gray-100 hover:opacity-80 transition-opacity"
                  >
                    <img
                      src={url}
                      alt={`Image ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Videos Section */}
          {videos.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                <Video className="w-4 h-4 mr-2" /> Videos ({videos.length})
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {videos.map((url, idx) => (
                  <a
                    key={idx}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center p-3 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <Video className="w-8 h-8 text-blue-500 mr-2" />
                    <span className="text-sm text-gray-700 truncate">Video {idx + 1}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* PDFs Section */}
          {pdfs.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                <FileText className="w-4 h-4 mr-2" /> Documents ({pdfs.length})
              </h3>
              <div className="space-y-2">
                {pdfs.map((url, idx) => (
                  <a
                    key={idx}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center p-3 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <FileText className="w-8 h-8 text-red-500 mr-2" />
                    <span className="text-sm text-gray-700 truncate">Document {idx + 1}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* No media message */}
          {images.length === 0 && videos.length === 0 && pdfs.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Image className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No media uploaded for this order</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Per-order-type exit labels (mirror backend routes/order_closure.py)
const EXIT_LABELS = {
  customer: { action: 'Deliver to Customer', done: 'Delivered to customer' },
  stock: { action: 'Move to Showroom Stock', done: 'Moved to showroom stock' },
  repair: { action: 'Return to Customer', done: 'Returned to customer' },
  inter_branch: { action: 'Send Back to Branch', done: 'Sent back to branch' },
};

// Order Details Panel Component
const OrderDetailsPanel = ({ order, isExpanded, onToggle, onOrderUpdate }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [isEditingCustomer, setIsEditingCustomer] = React.useState(false);
  const [customerForm, setCustomerForm] = React.useState({
    name: '',
    phone: '',
    address: '',
  });
  const [updating, setUpdating] = React.useState(false);
  const [linkedEstimates, setLinkedEstimates] = React.useState([]);
  const [loadingEstimates, setLoadingEstimates] = React.useState(false);

  // Phase 2 — closure
  const normType = (t) => {
    const r = (t || '').toString().toLowerCase();
    if (r.includes('repair')) return 'repair';
    if (r.includes('stock')) return 'stock';
    if (r.includes('branch') || r.includes('inter')) return 'inter_branch';
    if (['customer', 'stock', 'repair', 'inter_branch'].includes(r)) return r;
    return 'customer';
  };
  const canClose = ['admin', 'order_advisor'].includes(currentUser?.role);
  const isAdmin = currentUser?.role === 'admin';
  const [closeOpen, setCloseOpen] = React.useState(false);
  const [closing, setClosing] = React.useState(false);
  const [closeForm, setCloseForm] = React.useState({ handed_over_to: '', note: '' });
  const exitInfo = EXIT_LABELS[normType(order?.order_type)] || EXIT_LABELS.customer;

  const handleCloseOrder = async () => {
    if (!closeForm.handed_over_to.trim()) { alert('Please enter who the order is handed over to.'); return; }
    setClosing(true);
    try {
      await api.post(`/order-closure/${order.id}/close`, {
        handed_over_to: closeForm.handed_over_to.trim(),
        note: closeForm.note.trim() || null,
      });
      setCloseOpen(false);
      setCloseForm({ handed_over_to: '', note: '' });
      onOrderUpdate?.();
    } catch (e) {
      alert('❌ ' + (e.response?.data?.detail || 'Failed to close order'));
    } finally { setClosing(false); }
  };

  const handleReopen = async () => {
    if (!window.confirm('Reopen this closed order? Its previous status will be restored.')) return;
    try {
      await api.post(`/order-closure/${order.id}/reopen`);
      onOrderUpdate?.();
    } catch (e) {
      alert('❌ ' + (e.response?.data?.detail || 'Failed to reopen'));
    }
  };

  // Phase 3 — repair estimate (two-sided ledger)
  const isRepair = normType(order?.order_type) === 'repair';
  const canRepairEst = ['admin', 'owner', 'order_advisor', 'salesman'].includes(currentUser?.role);
  const REPAIR_WEIGHT_TYPES = ['new_gold', 'old_gold'];
  const [repairEsts, setRepairEsts] = React.useState([]);
  const [repairOpen, setRepairOpen] = React.useState(false);
  const [repairSeed, setRepairSeed] = React.useState(null);
  const [repairForceNew, setRepairForceNew] = React.useState(false);
  const [repairReviseFrom, setRepairReviseFrom] = React.useState(null);
  const [repairHistoryOpen, setRepairHistoryOpen] = React.useState(false);

  // Live production progress (job packets) — Phase 1 overlay shown on the order
  const PROGRESS_ROLES = ['admin', 'owner', 'production_supervisor', 'order_advisor', 'stock_supervisor', 'salesman'];
  const canSeeProgress = PROGRESS_ROLES.includes(currentUser?.role);
  const [jobProgress, setJobProgress] = React.useState(null);
  const loadJobProgress = React.useCallback(async () => {
    if (!canSeeProgress || !order?.id) return;
    try {
      const res = await api.get(`/order-jobs/for-order/${order.id}`);
      setJobProgress(res.data);
    } catch (e) { setJobProgress(null); }
  }, [canSeeProgress, order?.id]);
  React.useEffect(() => {
    if (isExpanded) loadJobProgress();
  }, [isExpanded, loadJobProgress]);
  const activeEst = React.useMemo(
    () => repairEsts.find((e) => e.status === 'active') || repairEsts[0] || null,
    [repairEsts],
  );

  const loadRepairEstimate = React.useCallback(async () => {
    if (!isRepair || !canRepairEst || !order?.id) return;
    try {
      const res = await api.get('/repair-estimates', { params: { order_id: order.id } });
      setRepairEsts(res.data || []);
    } catch (e) { /* noop */ }
  }, [isRepair, canRepairEst, order?.id]);

  const openRepairEditor = (est) => { setRepairForceNew(false); setRepairReviseFrom(null); setRepairSeed(est || null); setRepairOpen(true); };
  const openRepairRevise = async (src) => {
    let rate = null;
    try {
      const gp = await api.get('/gold-prices/latest');
      if (gp.data?.exists && gp.data.prices) rate = gp.data.prices['22K'] || Object.values(gp.data.prices)[0];
    } catch (e) { /* noop */ }
    const bump = (rows = []) => rows.map((r) => (REPAIR_WEIGHT_TYPES.includes(r.type) && rate ? { ...r, rate } : r));
    setRepairSeed({ ...src, gold_rate: rate ?? src.gold_rate, charges: bump(src.charges), credits: bump(src.credits) });
    setRepairReviseFrom(src.id); setRepairForceNew(true); setRepairOpen(true);
  };
  const reopenRepairEst = async (est) => {
    try {
      await api.post(`/repair-estimates/${est.id}/reopen`);
      await loadRepairEstimate();
      openRepairEditor({ ...est, status: 'draft' });
    } catch (e) { alert('❌ ' + (e.response?.data?.detail || 'Failed to reopen')); }
  };

  React.useEffect(() => {
    if (isExpanded) loadRepairEstimate();
  }, [isExpanded, loadRepairEstimate]);

  // Fetch linked estimates when order changes
  React.useEffect(() => {
    const fetchLinkedEstimates = async () => {
      if (order?.linked_estimate_ids && order.linked_estimate_ids.length > 0) {
        setLoadingEstimates(true);
        try {
          const estimatePromises = order.linked_estimate_ids.map(id =>
            api.get(`/estimates/by-id/${id}`).catch(() => null)
          );
          const results = await Promise.all(estimatePromises);
          const validEstimates = results.filter(r => r && r.data).map(r => r.data);
          setLinkedEstimates(validEstimates);
        } catch (error) {
          console.error('Error fetching linked estimates:', error);
        } finally {
          setLoadingEstimates(false);
        }
      } else {
        setLinkedEstimates([]);
      }
    };

    if (isExpanded) {
      fetchLinkedEstimates();
    }
  }, [order, isExpanded]);

  if (!order) return null;

  const handleEditClick = () => {
    setCustomerForm({
      name: order.customer_name || '',
      phone: order.customer_phone || '',
      address: order.customer_address || '',
    });
    setIsEditingCustomer(true);
  };

  const handleUpdateCustomer = async () => {
    setUpdating(true);
    try {
      await api.put(`/orders/${order.id}/customer`, customerForm);
      alert('✅ Customer details updated!');
      setIsEditingCustomer(false);
      window.location.reload(); // Refresh to show updated data
    } catch (error) {
      alert('❌ Error: ' + (error.response?.data?.detail || 'Failed to update'));
    } finally {
      setUpdating(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return null;
    return `₹${parseFloat(amount).toLocaleString('en-IN')}`;
  };

  return (
    <div className="bg-white border-b border-gray-200">
      {/* Toggle Header */}
      <button
        onClick={onToggle}
        className="w-full px-4 py-2 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
      >
        <span className="text-sm font-medium text-gray-700 flex items-center">
          📋 Order Details
        </span>
        <span className="text-gray-400 text-xs">
          {isExpanded ? '▲ Hide' : '▼ Show'}
        </span>
      </button>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Customer Info (hidden for restricted staff / vendors — Phase 2 privacy) */}
          {order.privacy_restricted ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center gap-2" data-testid="customer-hidden-note">
              <span className="text-gray-400">🔒</span>
              <p className="text-xs text-gray-500">Customer details are hidden for your role.</p>
            </div>
          ) : (
          <div className="bg-blue-50 rounded-lg p-3" data-testid="customer-info-block">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold text-blue-700">👤 Customer</h4>
              <button
                onClick={handleEditClick}
                className="text-blue-600 hover:text-blue-800 p-1"
              >
                <Edit2 className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-500">Name:</span>
                <span className="ml-1 font-medium">{order.customer_name || '-'}</span>
              </div>
              <div>
                <span className="text-gray-500">Phone:</span>
                <span className="ml-1 font-medium">{order.customer_phone || '-'}</span>
              </div>
              {order.customer_address && (
                <div className="col-span-2">
                  <span className="text-gray-500">Address:</span>
                  <span className="ml-1">{order.customer_address}</span>
                </div>
              )}
            </div>

            {/* Edit Customer Dialog */}
            {isEditingCustomer && (
              <Dialog open={isEditingCustomer} onOpenChange={setIsEditingCustomer}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Edit Customer Details</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Customer Name</Label>
                      <Input
                        value={customerForm.name}
                        onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })}
                        className="text-base py-6"
                      />
                    </div>
                    <div>
                      <Label>Phone Number</Label>
                      <Input
                        value={customerForm.phone}
                        onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })}
                        className="text-base py-6"
                      />
                    </div>
                    <div>
                      <Label>Address</Label>
                      <textarea
                        value={customerForm.address}
                        onChange={(e) => setCustomerForm({ ...customerForm, address: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 border rounded-md text-base"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end space-x-2 mt-4">
                    <Button variant="outline" onClick={() => setIsEditingCustomer(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleUpdateCustomer} disabled={updating} className="bg-blue-500">
                      {updating ? 'Updating...' : 'Update Customer'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
          )}

          {/* Order Info */}
          <div className="bg-emerald-50 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-emerald-700 mb-2">📦 Order Info</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-500">Order #:</span>
                <span className="ml-1 font-medium">{order.order_number}</span>
              </div>
              <div>
                <span className="text-gray-500">Type:</span>
                <span className="ml-1">{order.order_type || '-'}</span>
              </div>
              <div>
                <span className="text-gray-500">Product:</span>
                <span className="ml-1 font-medium">{order.title || '-'}</span>
              </div>
              <div>
                <span className="text-gray-500">Group:</span>
                <span className="ml-1">{order.main_group || '-'}</span>
              </div>
              {order.quantity && (
                <div>
                  <span className="text-gray-500">Qty:</span>
                  <span className="ml-1">{order.quantity}</span>
                </div>
              )}
              {order.product_weight && (
                <div>
                  <span className="text-gray-500">Weight:</span>
                  <span className="ml-1">{order.product_weight}g</span>
                </div>
              )}
              {order.metal_type && (
                <div>
                  <span className="text-gray-500">Metal:</span>
                  <span className="ml-1">{order.metal_type}</span>
                </div>
              )}
              {order.karat_purity && (
                <div>
                  <span className="text-gray-500">Purity:</span>
                  <span className="ml-1">{order.karat_purity}</span>
                </div>
              )}
            </div>
          </div>

          {/* Pricing (hidden for restricted staff / vendors — Phase 2 privacy) */}
          {!order.privacy_restricted && (order.price_amount || order.advance_payment || order.gold_rate_per_gram) && (
            <div className="bg-yellow-50 rounded-lg p-3" data-testid="pricing-block">
              <h4 className="text-xs font-semibold text-yellow-700 mb-2">💰 Pricing</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {order.gold_rate_type && (
                  <div>
                    <span className="text-gray-500">Rate Type:</span>
                    <span className="ml-1">{order.gold_rate_type === 'fixed' ? 'Fixed' : 'Open'}</span>
                  </div>
                )}
                {order.gold_rate_per_gram && (
                  <div>
                    <span className="text-gray-500">Gold Rate:</span>
                    <span className="ml-1">{formatCurrency(order.gold_rate_per_gram)}/g</span>
                  </div>
                )}
                {order.price_amount && (
                  <div>
                    <span className="text-gray-500">Total:</span>
                    <span className="ml-1 font-semibold text-green-600">{formatCurrency(order.price_amount)}</span>
                  </div>
                )}
                {order.advance_payment && (
                  <div>
                    <span className="text-gray-500">Advance:</span>
                    <span className="ml-1 text-blue-600">{formatCurrency(order.advance_payment)}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Linked Estimates */}
          {order.session_id && ['admin','owner','crm','salesman','stock_supervisor'].includes(currentUser?.role) && (
            <button
              onClick={() => navigate(`/lead-sessions/${order.session_id}`)}
              className="w-full flex items-center justify-between gap-2 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-lg px-3 py-2 transition-colors"
              data-testid="order-trace-session-link"
            >
              <span className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700">
                <ArrowLeft className="w-3.5 h-3.5" /> View originating lead session
              </span>
              <span className="text-[11px] text-emerald-600">Open session →</span>
            </button>
          )}
          {order.linked_estimate_ids && order.linked_estimate_ids.length > 0 && (
            <div className="bg-purple-50 rounded-lg p-3">
              <h4 className="text-xs font-semibold text-purple-700 mb-2">📋 Linked Estimates</h4>
              {loadingEstimates ? (
                <p className="text-sm text-gray-500">Loading estimates...</p>
              ) : (
                <div className="space-y-3">
                  {linkedEstimates.length > 0 ? (
                    linkedEstimates.map((estimate) => (
                      <div key={estimate.id} className="bg-white rounded-lg p-3 border-2 border-purple-300">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-3 pb-2 border-b">
                          <div>
                            <p className="text-sm font-bold text-purple-700">{estimate.estimate_number}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(estimate.created_at).toLocaleDateString('en-IN', {
                                day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata'
                              })}
                            </p>
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                            estimate.status === 'final'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            {estimate.status?.toUpperCase()}
                          </span>
                        </div>

                        {/* Product Info */}
                        {(estimate.main_group || estimate.product) && (
                          <div className="mb-2">
                            <p className="text-xs font-semibold text-gray-700">
                              {estimate.main_group} {estimate.product && `• ${estimate.product}`}
                            </p>
                          </div>
                        )}

                        {/* Weight Details */}
                        <div className="bg-blue-50 rounded p-2 mb-2">
                          <p className="text-xs font-semibold text-blue-700 mb-1">⚖️ Weight Breakdown</p>
                          <div className="grid grid-cols-2 gap-1 text-xs">
                            <div><span className="text-gray-600">Gross:</span> <span className="font-medium">{estimate.gross_weight}g</span></div>
                            <div><span className="text-gray-600">Stone:</span> <span className="font-medium">{estimate.stones_weight_grams?.toFixed(2)}g</span></div>
                            <div><span className="text-gray-600">Net:</span> <span className="font-medium">{estimate.net_weight}g</span></div>
                            <div><span className="text-gray-600">+Wastage:</span> <span className="font-medium">{estimate.gold_weight_with_wastage?.toFixed(2)}g</span></div>
                          </div>
                        </div>

                        {/* Stones Details */}
                        {estimate.stones && estimate.stones.length > 0 && (
                          <div className="bg-pink-50 rounded p-2 mb-2">
                            <p className="text-xs font-semibold text-pink-700 mb-1">💎 Stones ({estimate.stones.length})</p>
                            <div className="space-y-1">
                              {estimate.stones.map((stone, idx) => (
                                <div key={idx} className="text-xs bg-white rounded px-2 py-1">
                                  <span className="font-medium">{stone.name}</span>
                                  <span className="text-gray-600"> - {stone.weight}ct × ₹{stone.rate?.toLocaleString('en-IN')} = </span>
                                  <span className="font-semibold text-pink-600">₹{stone.amount?.toLocaleString('en-IN')}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Price Breakdown */}
                        <div className="bg-green-50 rounded p-2">
                          <p className="text-xs font-semibold text-green-700 mb-1">💰 Price Breakdown</p>
                          <div className="space-y-0.5 text-xs">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Gold ({estimate.gold_purity}):</span>
                              <span className="font-medium">₹{estimate.gold_amount?.toLocaleString('en-IN', {maximumFractionDigits: 0})}</span>
                            </div>
                            {estimate.stones_amount > 0 && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Stones:</span>
                                <span className="font-medium">₹{estimate.stones_amount?.toLocaleString('en-IN', {maximumFractionDigits: 0})}</span>
                              </div>
                            )}
                            {estimate.making_amount > 0 && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Making:</span>
                                <span className="font-medium">₹{estimate.making_amount?.toLocaleString('en-IN', {maximumFractionDigits: 0})}</span>
                              </div>
                            )}
                            {estimate.other_charges > 0 && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">Other:</span>
                                <span className="font-medium">₹{estimate.other_charges?.toLocaleString('en-IN', {maximumFractionDigits: 0})}</span>
                              </div>
                            )}
                            <div className="flex justify-between border-t pt-1">
                              <span className="text-gray-600">Subtotal:</span>
                              <span className="font-medium">₹{estimate.subtotal?.toLocaleString('en-IN', {maximumFractionDigits: 0})}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">GST (3%):</span>
                              <span className="font-medium">₹{estimate.gst_amount?.toLocaleString('en-IN', {maximumFractionDigits: 0})}</span>
                            </div>
                            <div className="flex justify-between border-t-2 border-green-300 pt-1 mt-1">
                              <span className="font-semibold text-green-700">TOTAL:</span>
                              <span className="font-bold text-green-700 text-sm">₹{estimate.total_amount?.toLocaleString('en-IN', {maximumFractionDigits: 0})}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">
                      {order.linked_estimate_ids.length} estimate(s) linked (unable to load details)
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Delivery & Status */}
          <div className="bg-orange-50 rounded-lg p-3">
            <h4 className="text-xs font-semibold text-orange-700 mb-2">📅 Delivery & Status</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {order.delivery_date && (
                <div>
                  <span className="text-gray-500">Delivery:</span>
                  <span className="ml-1 font-medium">{new Date(order.delivery_date).toLocaleDateString('en-IN', {
                    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata'
                  })}</span>
                </div>
              )}
              <div>
                <span className="text-gray-500">Status:</span>
                <span className={`ml-1 font-medium ${
                  order.order_status === 'Completed' ? 'text-green-600' :
                  order.order_status === 'Pending' ? 'text-orange-600' : 'text-blue-600'
                }`}>{order.order_status || 'Pending'}</span>
              </div>
              {order.hallmarking_needed !== undefined && (
                <div>
                  <span className="text-gray-500">Hallmark:</span>
                  <span className="ml-1">{order.hallmarking_needed ? 'Yes' : 'No'}</span>
                </div>
              )}
            </div>
          </div>

          {/* Production Progress (Phase 1 job packets — live SLA) */}
          {canSeeProgress && jobProgress && jobProgress.summary.total > 0 && (
            <div className="rounded-lg p-3 border bg-indigo-50 border-indigo-200" data-testid="order-progress-block">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-semibold text-indigo-700">🛠️ Production Progress</h4>
                <span className="text-xs text-indigo-600 font-medium" data-testid="order-progress-percent">{jobProgress.summary.percent_done}% done</span>
              </div>
              <div className="h-2 rounded-full bg-indigo-100 overflow-hidden mb-2">
                <div className="h-full bg-indigo-500 transition-all" style={{ width: `${jobProgress.summary.percent_done}%` }} />
              </div>
              <p className="text-xs text-gray-500 mb-2">
                {jobProgress.summary.done}/{jobProgress.summary.total} tasks done · {jobProgress.summary.active} active
                {jobProgress.summary.overdue > 0 && <span className="text-red-600 font-medium"> · {jobProgress.summary.overdue} overdue</span>}
              </p>
              <div className="space-y-1" data-testid="order-progress-list">
                {jobProgress.jobs.map((j) => (
                  <div key={j.id} className="flex items-center justify-between text-xs bg-white/60 rounded px-2 py-1.5">
                    <span className="flex items-center gap-2 min-w-0">
                      <span className={`w-2 h-2 rounded-full shrink-0 ${j.status === 'done' ? 'bg-emerald-500' : j.status === 'in_progress' ? 'bg-blue-500' : j.status === 'awaiting_qc' ? 'bg-amber-500' : j.status === 'sent' ? 'bg-indigo-500' : j.status === 'on_hold' ? 'bg-orange-500' : 'bg-slate-300'}`} />
                      <span className="font-medium text-slate-700 truncate">{j.checkpoint_name}</span>
                      <span className="text-slate-400 truncate">· {j.owner_name}</span>
                    </span>
                    <span className={`shrink-0 ${j.overdue ? 'text-red-600 font-medium' : 'text-slate-400'}`}>
                      {j.status === 'done' ? '✓ done' : j.overdue ? `overdue ${Math.abs(j.days_left ?? 0)}d` : (j.days_left != null ? `${j.days_left}d left` : '—')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Repair Estimate (Phase 3 — only on repair orders, front-office roles) */}
          {isRepair && canRepairEst && (
            <div className="rounded-lg p-3 border bg-teal-50 border-teal-200" data-testid="repair-estimate-block">
              <div className="flex items-center justify-between mb-1">
                <h4 className="text-xs font-semibold text-teal-700">
                  🔧 Repair Estimate
                  {repairEsts.length > 1 && <span className="ml-1 text-gray-400 font-normal">· {repairEsts.length} versions</span>}
                </h4>
                {!activeEst && (
                  <button onClick={() => openRepairEditor(null)} className="text-xs px-3 py-1.5 rounded-full bg-teal-600 text-white font-semibold hover:bg-teal-700" data-testid="open-repair-estimate-btn">
                    Create estimate
                  </button>
                )}
              </div>
              {activeEst ? (
                <div className="text-sm space-y-2" data-testid="repair-estimate-summary">
                  <div>
                    <p className={`font-bold ${activeEst.customer_pays ? 'text-emerald-700' : 'text-red-600'}`}>
                      {activeEst.customer_pays ? 'Customer pays' : 'We refund'} ₹{Math.abs(Math.round(activeEst.net_amount || 0)).toLocaleString('en-IN')}
                      <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded-full align-middle ${activeEst.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                        {activeEst.status === 'active' ? 'Active' : activeEst.status === 'superseded' ? 'Superseded' : 'Draft'}
                      </span>
                    </p>
                    <p className="text-xs text-gray-500">
                      Charges ₹{Math.round(activeEst.subtotal_charges || 0).toLocaleString('en-IN')} · GST {activeEst.gst_percent}% · Credits ₹{Math.round(activeEst.subtotal_credits || 0).toLocaleString('en-IN')}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => openRepairEditor(activeEst)} className="text-xs px-3 py-1.5 rounded-full border border-teal-300 text-teal-700 hover:bg-teal-100" data-testid="open-repair-estimate-btn">
                      View / edit
                    </button>
                    {activeEst.status !== 'draft' && (
                      <>
                        <button onClick={() => openRepairRevise(activeEst)} className="text-xs px-3 py-1.5 rounded-full bg-teal-600 text-white font-semibold hover:bg-teal-700" data-testid="revise-repair-estimate-btn">
                          Revise at today's rate
                        </button>
                        <button onClick={() => reopenRepairEst(activeEst)} className="text-xs px-3 py-1.5 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-100" data-testid="reopen-repair-estimate-btn">
                          Reopen
                        </button>
                      </>
                    )}
                    <button onClick={() => printRepairEstimate(activeEst)} className="text-xs px-3 py-1.5 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-100" data-testid="print-repair-estimate-btn">
                      Print slip
                    </button>
                    {repairEsts.length > 1 && (
                      <button onClick={() => setRepairHistoryOpen((v) => !v)} className="text-xs px-3 py-1.5 rounded-full border border-gray-300 text-gray-500 hover:bg-gray-100" data-testid="repair-history-toggle">
                        {repairHistoryOpen ? 'Hide history' : `Show history (${repairEsts.length})`}
                      </button>
                    )}
                  </div>
                  {repairHistoryOpen && repairEsts.length > 1 && (
                    <div className="mt-2 space-y-1 border-t border-teal-200 pt-2" data-testid="repair-history-list">
                      {repairEsts.map((e) => (
                        <div key={e.id} className="flex items-center justify-between text-xs bg-white/60 rounded px-2 py-1">
                          <span className="flex items-center gap-2">
                            <span className="font-mono text-gray-400">{e.estimate_number || e.id.slice(-6)}</span>
                            <span className={e.customer_pays ? 'text-emerald-700' : 'text-red-600'}>
                              {e.customer_pays ? 'Pay' : 'Refund'} ₹{Math.abs(Math.round(e.net_amount || 0)).toLocaleString('en-IN')}
                            </span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${e.status === 'active' ? 'bg-green-100 text-green-700' : e.status === 'superseded' ? 'bg-gray-200 text-gray-500' : 'bg-amber-100 text-amber-700'}`}>
                              {e.status === 'active' ? 'Active' : e.status === 'superseded' ? 'Superseded' : 'Draft'}
                            </span>
                          </span>
                          <span className="flex gap-2">
                            <button onClick={() => openRepairEditor(e)} className="text-teal-600 hover:underline" data-testid="repair-history-view">View</button>
                            <button onClick={() => printRepairEstimate(e)} className="text-gray-500 hover:underline" data-testid="repair-history-print">Print</button>
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-xs text-gray-500">No estimate yet. Build the add/remove ledger to compute what the customer pays or gets refunded.</p>
              )}
            </div>
          )}
          {repairOpen && (
            <RepairEstimateDialog
              open={repairOpen}
              onOpenChange={setRepairOpen}
              orderId={order.id}
              orderNumber={order.order_number}
              estimate={repairSeed}
              forceNew={repairForceNew}
              reviseFrom={repairReviseFrom}
              onSaved={() => { loadRepairEstimate(); onOrderUpdate?.(); }}
            />
          )}

          {/* Delivery Closure (Phase 2) */}
          {(canClose || order.closed) && (
            <div className={`rounded-lg p-3 border ${order.closed ? 'bg-green-50 border-green-200' : 'bg-teal-50 border-teal-200'}`} data-testid="closure-block">
              <h4 className={`text-xs font-semibold mb-2 ${order.closed ? 'text-green-700' : 'text-teal-700'}`}>🚚 Delivery / Closure</h4>
              {order.closed && order.closure ? (
                <div className="text-sm space-y-1">
                  <p className="font-semibold text-green-700" data-testid="closure-status">{order.closure.exit_label || order.order_status}</p>
                  <p className="text-gray-600">Handed to: <span className="font-medium">{order.closure.handed_over_to}</span></p>
                  {order.closure.repair_payment && (
                    <p className={`font-semibold ${order.closure.repair_payment.customer_pays ? 'text-emerald-700' : 'text-red-600'}`} data-testid="closure-repair-payment">
                      {order.closure.repair_payment.customer_pays ? 'Collected' : 'Refunded'}: ₹{Math.abs(Math.round(order.closure.repair_payment.net_amount || 0)).toLocaleString('en-IN')}
                    </p>
                  )}
                  {order.closure.note && <p className="text-gray-500 text-xs">Note: {order.closure.note}</p>}
                  <p className="text-gray-400 text-xs">
                    Closed by {order.closure.closed_by_name}
                    {order.closure.closed_at && ` • ${new Date(order.closure.closed_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })}`}
                  </p>
                  {isAdmin && (
                    <button onClick={handleReopen} data-testid="reopen-order-btn" className="mt-2 text-xs px-3 py-1.5 rounded-full border border-green-300 text-green-700 hover:bg-green-100">
                      Reopen order
                    </button>
                  )}
                </div>
              ) : (
                <div className="text-sm">
                  <p className="text-gray-600 mb-2">This order is still open. Closing it will mark it as <span className="font-semibold">{exitInfo.done}</span>.</p>
                  <button onClick={() => setCloseOpen(true)} data-testid="close-order-btn" className="text-xs px-3 py-2 rounded-full bg-teal-600 text-white font-semibold hover:bg-teal-700">
                    {exitInfo.action}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Close Order Dialog */}
          {closeOpen && (
            <Dialog open={closeOpen} onOpenChange={(o) => !closing && setCloseOpen(o)}>
              <DialogContent data-testid="close-order-dialog">
                <DialogHeader>
                  <DialogTitle>{exitInfo.action}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-gray-500">Order <b>{order.order_number}</b> will be marked as <b>{exitInfo.done}</b>.</p>
                  {isRepair && (order.repair_net_amount != null ? (
                    <div className={`rounded-lg p-3 border ${order.repair_customer_pays ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`} data-testid="close-repair-payment">
                      <p className="text-xs text-gray-500">Active repair estimate{activeEst?.estimate_number ? ` · ${activeEst.estimate_number}` : ''}</p>
                      <p className={`text-lg font-bold ${order.repair_customer_pays ? 'text-emerald-700' : 'text-red-600'}`}>
                        {order.repair_customer_pays ? 'Collect from customer' : 'Refund to customer'}: ₹{Math.abs(Math.round(order.repair_net_amount)).toLocaleString('en-IN')}
                      </p>
                    </div>
                  ) : (
                    <div className="rounded-lg p-2 border bg-amber-50 border-amber-200 text-xs text-amber-700" data-testid="close-repair-nopayment">
                      No confirmed repair estimate yet — confirm one to record the exact amount to collect or refund.
                    </div>
                  ))}
                  <div>
                    <Label>Handed over to *</Label>
                    <Input
                      value={closeForm.handed_over_to}
                      onChange={(e) => setCloseForm({ ...closeForm, handed_over_to: e.target.value })}
                      placeholder="e.g. customer name / branch / showroom"
                      className="text-base py-6"
                      data-testid="close-handed-to-input"
                    />
                  </div>
                  <div>
                    <Label>Note (optional)</Label>
                    <textarea
                      value={closeForm.note}
                      onChange={(e) => setCloseForm({ ...closeForm, note: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border rounded-md text-base"
                      placeholder="Any closing remarks"
                      data-testid="close-note-input"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-2 mt-4">
                  <Button variant="outline" onClick={() => setCloseOpen(false)} disabled={closing}>Cancel</Button>
                  <Button onClick={handleCloseOrder} disabled={closing} className="bg-teal-600 hover:bg-teal-700" data-testid="confirm-close-btn">
                    {closing ? 'Closing…' : `Confirm — ${exitInfo.action}`}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}

          {/* Stone Details */}
          {order.stone_details && (
            <div className="bg-purple-50 rounded-lg p-3">
              <h4 className="text-xs font-semibold text-purple-700 mb-2">💎 Stone Details</h4>
              <p className="text-sm text-gray-700">{order.stone_details}</p>
            </div>
          )}

          {/* Solitaires */}
          {order.solitaires && order.solitaires.length > 0 && (
            <div className="bg-indigo-50 rounded-lg p-3">
              <h4 className="text-xs font-semibold text-indigo-700 mb-2">
                💎 Solitaires ({order.solitaires.length})
              </h4>
              <div className="space-y-2">
                {order.solitaires.map((sol, idx) => (
                  <div key={idx} className="bg-white rounded p-2 border border-indigo-100">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className="font-medium text-sm">
                          #{idx + 1} {sol.shape}
                          {sol.size_from && sol.size_to && ` ${sol.size_from}-${sol.size_to}ct`}
                        </span>
                        <div className="text-xs text-gray-500 mt-1">
                          {[
                            sol.color_grades?.length > 0 ? `Color: ${sol.color_grades.join('')}` : null,
                            sol.clarities?.length > 0 ? `Clarity: ${sol.clarities.join('/')}` : null,
                            sol.cut ? `Cut: ${sol.cut}` : null,
                          ].filter(Boolean).join(' | ')}
                        </div>
                        {sol.labs?.length > 0 && (
                          <div className="text-xs text-gray-500">
                            Cert: {sol.labs.join(', ')}
                          </div>
                        )}
                        {sol.notes && (
                          <div className="text-xs text-gray-400 mt-1">{sol.notes}</div>
                        )}
                      </div>
                      {sol.estimate_amount && (
                        <span className="text-sm font-semibold text-green-600">
                          {formatCurrency(sol.estimate_amount)}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Special Instructions */}
          {order.special_instructions && (
            <div className="bg-gray-50 rounded-lg p-3">
              <h4 className="text-xs font-semibold text-gray-700 mb-2">📝 Special Instructions</h4>
              <p className="text-sm text-gray-700">{order.special_instructions}</p>
            </div>
          )}

          {/* Estimate Images */}
          {order.estimate_images && order.estimate_images.length > 0 && (
            <div className="bg-amber-50 rounded-lg p-3">
              <h4 className="text-xs font-semibold text-amber-700 mb-2 flex items-center">
                🖼️ Estimate Images ({order.estimate_images.length})
              </h4>
              <div className="grid grid-cols-3 gap-2">
                {order.estimate_images.map((imgUrl, idx) => (
                  <a
                    key={idx}
                    href={imgUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="aspect-square rounded overflow-hidden border-2 border-amber-200 hover:border-amber-400 transition-all"
                  >
                    <img
                      src={imgUrl}
                      alt={`Estimate ${idx + 1}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform"
                    />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const MessageBubble = ({ message, isOwn, order }) => {
  const { currentUser } = useAuth();
  const isRead = message.read_by?.length > 0 && message.read_by.some(id => id !== message.sender_id);
  const isSystem = message.type === 'system';
  const isOrderCreated = message.type === 'order_created';

  // Order created message with print buttons
  if (isOrderCreated && order) {
    return (
      <div className="flex justify-center mb-3">
        <div className="bg-green-50 border border-green-200 text-green-800 text-sm px-4 py-3 rounded-lg max-w-[85%] text-center shadow-sm">
          <p className="font-semibold text-base">{message.content}</p>
          <div className="flex gap-2 justify-center mt-2">
            <button
              onClick={() => printOrderForm(order, 'customer')}
              className="flex items-center gap-1 px-3 py-1.5 bg-white border border-green-300 rounded-lg text-xs font-medium text-green-700 hover:bg-green-100 transition-colors"
              data-testid="msg-print-customer"
            >
              <Printer className="w-3 h-3" /> Customer Copy
            </button>
            <button
              onClick={() => printOrderForm(order, 'production')}
              className="flex items-center gap-1 px-3 py-1.5 bg-white border border-green-300 rounded-lg text-xs font-medium text-green-700 hover:bg-green-100 transition-colors"
              data-testid="msg-print-production"
            >
              <Printer className="w-3 h-3" /> Production Copy
            </button>
          </div>
          <p className="text-green-500 mt-2 text-xs">
            {message.sender_name} • {message.timestamp && formatDistanceToNow(new Date(message.timestamp + 'Z'), { addSuffix: true })}
          </p>
        </div>
      </div>
    );
  }

  // Completion upload message
  if (message.type === 'completion_upload' && order) {
    const v = order.completion_verification;
    return (
      <div className="flex justify-center mb-3">
        <div className="bg-green-50 border border-green-200 text-green-800 text-sm px-4 py-3 rounded-lg max-w-[85%] shadow-sm">
          <p className="font-semibold text-center">{message.content}</p>
          {v?.finished_image && (
            <img src={v.finished_image} alt="Finished product" className="w-full max-w-[200px] mx-auto mt-2 rounded-lg border" />
          )}
          {(v?.final_gross_weight || v?.final_net_weight) && (
            <div className="flex justify-center gap-4 mt-2 text-xs">
              {v.final_gross_weight && <span>Gross: {v.final_gross_weight}g</span>}
              {v.final_net_weight && <span>Net: {v.final_net_weight}g</span>}
              {v.purity_verified && <span>Purity: {v.purity_verified}</span>}
            </div>
          )}
          <p className="text-green-500 mt-2 text-xs text-center">
            {message.sender_name} • {message.timestamp && formatDistanceToNow(new Date(message.timestamp + 'Z'), { addSuffix: true })}
          </p>
        </div>
      </div>
    );
  }

  // System message styling
  if (isSystem) {
    return (
      <div className="flex justify-center mb-3">
        <div className="bg-blue-50 border border-blue-200 text-blue-800 text-sm px-4 py-2 rounded-lg max-w-[80%] text-center shadow-sm">
          <p className="whitespace-pre-wrap text-base">{message.content}</p>
          <p className="text-blue-500 mt-1 text-sm">
            {message.sender_name} • {message.timestamp && formatDistanceToNow(new Date(message.timestamp + 'Z'), { addSuffix: true })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-2 group`}>
      <div className={`max-w-[65%] ${isOwn ? 'order-2' : 'order-1'}`}>
        {!isOwn && (
          <p className="text-sm text-gray-600 mb-1 ml-2 font-medium">
            {message.sender_name}
          </p>
        )}
        <div
          className={`rounded-lg px-4 py-2 shadow-sm ${
            isOwn
              ? 'bg-emerald-500 text-white rounded-tr-none'
              : 'bg-white text-gray-800 rounded-tl-none'
          }`}
        >
          {message.type === 'text' && (
            <p className="text-base break-words whitespace-pre-wrap">{message.content}</p>
          )}
          {message.type === 'image' && (
            <div className="space-y-2">
              <img
                src={message.content}
                alt="Shared image"
                className="rounded-lg max-w-full h-auto"
              />
            </div>
          )}
          {message.type === 'voice' && (
            <div className="flex items-center space-x-2 py-1">
              <audio controls className="max-w-full">
                <source src={message.content} type="audio/webm" />
                <source src={message.content} type="audio/ogg" />
                <source src={message.content} type="audio/mpeg" />
                Your browser does not support audio playback.
              </audio>
            </div>
          )}
          {message.type === 'file' && (
            <div className="flex items-center space-x-3 py-2">
              <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                <Download className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm font-medium">File attachment</p>
                <p className="text-xs opacity-75">Click to download</p>
              </div>
            </div>
          )}
          <div className="flex items-center justify-end space-x-1 mt-1">
            <span className={`text-sm ${
              isOwn ? 'text-emerald-100' : 'text-gray-500'
            }`}>
              {message.timestamp && formatDistanceToNow(new Date(message.timestamp + 'Z'), { addSuffix: true })}
            </span>
            {isOwn && (
              <span>
                {isRead ? (
                  <CheckCheck className="w-5 h-5 text-blue-400" />
                ) : (
                  <Check className="w-5 h-5 text-emerald-100" />
                )}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ChatArea = ({ isMobile = false, onBack }) => {
  const { selectedOrder, getOrderMessages, typingUsers, refreshOrders } = useChat();
  const { currentUser } = useAuth();
  const messagesEndRef = useRef(null);
  const [mediaGalleryOpen, setMediaGalleryOpen] = useState(false);
  const [orderDetailsExpanded, setOrderDetailsExpanded] = useState(false);

  // Get messages directly from context instead of local state
  const orderMessages = selectedOrder ? getOrderMessages(selectedOrder.id) : [];

  // Get first image thumbnail
  const firstImage = selectedOrder?.uploaded_files?.find(f => getFileType(f) === 'image');

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [orderMessages]);

  const handleOrderUpdate = () => {
    // Refresh orders to get updated data
    refreshOrders?.();
  };

  if (!selectedOrder) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center px-4">
          <div className="w-24 h-24 md:w-32 md:h-32 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-12 h-12 md:w-16 md:h-16 text-emerald-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
          </div>
          <h2 className="text-xl md:text-2xl font-semibold text-gray-700 mb-2">OrderChat Messenger</h2>
          <p className="text-gray-500 text-sm md:text-base">Select an order to start chatting</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-[#e5ddd5] bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0icGF0dGVybiIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSIgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiPjxwYXRoIGQ9Ik0gMCAwIEwgMTAwIDAgTCAxMDAgMTAwIEwgMCAxMDAgWiIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjZDFkN2RiIiBzdHJva2Utd2lkdGg9IjAuNSIgb3BhY2l0eT0iMC4xIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI3BhdHRlcm4pIi8+PC9zdmc+')] overflow-hidden">
      {/* Sticky Header Section */}
      <div className="flex-shrink-0">
        {/* Chat Header - Tappable to open media gallery */}
        <div
          className="bg-[#075E54] text-white px-4 md:px-6 py-3 shadow-md cursor-pointer hover:bg-[#064d47] transition-colors"
          onClick={() => setMediaGalleryOpen(true)}
        >
        <div className="flex items-center space-x-3">
          {/* Back button for mobile */}
          {isMobile && onBack && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onBack();
              }}
              className="p-1 hover:bg-emerald-700 rounded-full transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
          )}

          {/* Order Thumbnail */}
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-full overflow-hidden bg-emerald-700 flex-shrink-0 flex items-center justify-center">
            {firstImage ? (
              <img
                src={firstImage}
                alt="Order"
                className="w-full h-full object-cover"
              />
            ) : (
              <Image className="w-5 h-5 md:w-6 md:h-6 text-emerald-300" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h2 className="text-base md:text-lg font-semibold truncate">{selectedOrder.order_number}</h2>
            {selectedOrder.auto_generated_number && (
              <p className="text-xs text-emerald-200 font-mono truncate">
                {selectedOrder.auto_generated_number}
              </p>
            )}
            <p className="text-xs md:text-sm text-emerald-100 truncate">
              {selectedOrder.title}
              {selectedOrder.uploaded_files?.length > 0 && (
                <span className="ml-2">📎 {selectedOrder.uploaded_files.length} files</span>
              )}
            </p>
            {(() => {
              const flag = getDeadlineFlag(selectedOrder);
              if (flag.level === 'gray') return null;
              return <span className={`inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold border ${DEADLINE_STYLES[flag.level]}`}>{flag.label}</span>;
            })()}
          </div>

          {/* Print Button */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                onClick={(e) => e.stopPropagation()}
                className="p-2 hover:bg-emerald-700 rounded-full transition-colors flex-shrink-0"
                data-testid="print-order-btn"
              >
                <Printer className="w-5 h-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem onClick={() => printOrderForm(selectedOrder, 'customer')} data-testid="print-customer-copy">
                Customer Copy
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => printOrderForm(selectedOrder, 'production')} data-testid="print-production-copy">
                Production Copy
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Media Gallery Dialog */}
      <MediaGallery
        isOpen={mediaGalleryOpen}
        onClose={() => setMediaGalleryOpen(false)}
        files={selectedOrder.uploaded_files}
        orderNumber={selectedOrder.order_number}
      />

      {/* Production Panel */}
      <ProductionPanel order={selectedOrder} onOrderUpdate={handleOrderUpdate} isMobile={isMobile} />

      {/* Order Details Panel */}
      <OrderDetailsPanel
        order={selectedOrder}
        isExpanded={orderDetailsExpanded}
        onToggle={() => setOrderDetailsExpanded(!orderDetailsExpanded)}
        onOrderUpdate={handleOrderUpdate}
      />
      </div>

      {/* Scrollable Messages Area */}
      <div className="flex-1 overflow-y-auto px-3 md:px-6 py-4">
        {orderMessages.map((message) => (
          <MessageBubble
            key={message.id}
            message={message}
            isOwn={message.sender_id === currentUser?.id}
            order={selectedOrder}
          />
        ))}
        {typingUsers[selectedOrder.id] && (
          <div className="flex items-center space-x-2 text-gray-600 text-sm mb-2">
            <div className="flex space-x-1">
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
            </div>
            <span>Someone is typing...</span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

export default ChatArea;
